/**
 * JourFx Admin Portal
 * Main entry point with Convex + Better Auth integration
 */
import './i18n';
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { ConvexReactClient } from "convex/react";
import { ConvexBetterAuthProvider } from "@convex-dev/better-auth/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { authClient } from "./lib/auth";
import { routeTree } from "./routeTree";
import { ErrorBoundary, SkipLink } from "@jourfx/ui";
import "./index.css";

// Initialize Convex client
const convex = new ConvexReactClient(
  import.meta.env.VITE_CONVEX_URL || "http://localhost:3210"
);

// Initialize React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// Create router
const router = createRouter({ routeTree });

// Type safety for router
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Mount the app - prevent duplicate root creation during HMR
const container = document.getElementById("root")!;
const root = (window as any).__REACT_ROOT__ || ReactDOM.createRoot(container);
(window as any).__REACT_ROOT__ = root;

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <SkipLink />
      <Suspense fallback={<div className="flex h-screen items-center justify-center">Laden...</div>}>
        <ConvexBetterAuthProvider client={convex} authClient={authClient}>
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </ConvexBetterAuthProvider>
      </Suspense>
    </ErrorBoundary>
  </React.StrictMode>
);
